import { supabase } from '../../config/supabaseClient';
import { ENV } from '../../config/env';

export type PmIntakeTargetType = 'task' | 'project';

export interface PmIntakeForm {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  target_type: PmIntakeTargetType;
  config: Record<string, any>;
  is_active: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
}

async function getToken(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');
  return session.access_token;
}

const API_BASE_URL = `${ENV.API_URL}/api/project-management/intake-forms`;

export const pmIntakeFormsApiService = {
  async list(organizationId: string): Promise<{ data: PmIntakeForm[]; error: any }> {
    try {
      const token = await getToken();
      const queryParams = new URLSearchParams({ organization_id: organizationId });

      const response = await fetch(`${API_BASE_URL}?${queryParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      if (!response.ok) {
        return { data: [], error: result?.error || 'Failed to fetch intake forms' };
      }

      return { data: (result.data || []) as PmIntakeForm[], error: null };
    } catch (e: any) {
      return { data: [], error: e?.message || 'Network error' };
    }
  },

  async create(input: {
    organization_id: string;
    name: string;
    description?: string | null;
    target_type: PmIntakeTargetType;
    config?: Record<string, any>;
    is_active?: boolean;
  }): Promise<{ data: PmIntakeForm | null; error: any }> {
    try {
      const token = await getToken();

      const response = await fetch(API_BASE_URL, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(input),
      });

      const result = await response.json();
      if (!response.ok) {
        return { data: null, error: result?.error || 'Failed to create intake form' };
      }

      return { data: (result.data || null) as PmIntakeForm | null, error: null };
    } catch (e: any) {
      return { data: null, error: e?.message || 'Network error' };
    }
  },

  async delete(formId: string, organizationId: string): Promise<{ error: any }> {
    try {
      const token = await getToken();
      const queryParams = new URLSearchParams({ organization_id: organizationId });

      const response = await fetch(
        `${API_BASE_URL}/${encodeURIComponent(formId)}?${queryParams.toString()}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const result = await response.json();
      if (!response.ok) {
        return { error: result?.error || 'Failed to delete intake form' };
      }

      return { error: null };
    } catch (e: any) {
      return { error: e?.message || 'Network error' };
    }
  },

  async submit(
    formId: string,
    input: {
      organization_id: string;
      payload: Record<string, any>;
    }
  ): Promise<{ data: any; error: any }> {
    try {
      const token = await getToken();

      const response = await fetch(`${API_BASE_URL}/${encodeURIComponent(formId)}/submit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(input),
      });

      const result = await response.json();
      if (!response.ok) {
        return { data: null, error: result?.error || 'Failed to submit intake form' };
      }

      return { data: result.data, error: null };
    } catch (e: any) {
      return { data: null, error: e?.message || 'Network error' };
    }
  },
};

export default pmIntakeFormsApiService;
